import { useEffect, useRef, useState } from "react";
import { Col, Row, Form, Modal, Accordion } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import DefaultDatePicker from "../../common/datepicker/DefaultDatePicker.jsx";
import { statementReportPost, statementTemplatePost } from "../../api";
import { localized } from "../../common/utils";
import { PersonnelStructure } from "./personnelstructure";
import { LoadingButton } from "../../common/loaders/loadingbutton";
import { useDispatch } from "react-redux";
import { buildAlert, buildAlertWithLocalizedMessages } from "../../actions";
import { hrEsalary } from "../../main";
import { Button } from "../../common/Button.tsx";

const storageKey = "statementDefaults";
const defaultPrompt =
  "Kirjoita kattava tekstimuotoinen henkilöstökertomus HTML-muodossa hyödyntäen annettuja faktoja. Älä muodosta taulukoita. Anna samalla yleiskatsaus kyseessä olevan yrityksen nykytilanteesta.";
const lastYear = new Date().getFullYear() - 1;

export function NewStatementModal({ setShowModal, templatesMap, fetchData }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const prefix = "newStatementModal";
  const closeModal = () => setShowModal(false);

  const [showUpload, setShowUpload] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [templateFileDisabled, setTemplateFileDisabled] = useState(true);
  const [generatingReport, setGeneratingReport] = useState(false);

  const { register, control, handleSubmit, setValue } = useForm({
    defaultValues: JSON.parse(localStorage.getItem(storageKey), (key, value) =>
      key === "start" || key === "end" ? `${value}-01` : value,
    ) ?? {
      // reviver
      templateId: 1,
      name: lastYear,
      start: `${lastYear}-01-01`,
      end: `${lastYear}-12-31`,
    }, // if not saved in localStorage
  });

  async function uploadTemplate(e) {
    setShowUpload(false);

    const formData = new FormData();
    formData.append("file", e.target.files[0]);
    const id = await statementTemplatePost(templateName, formData);

    await fetchData();
    setValue("templateId", id);
    setTemplateName("");
  }

  const templateOptions = [];
  for (const id in templatesMap) {
    templateOptions.push(
      <option key={id} value={id}>
        {templatesMap[id]}
      </option>,
    );
  }

  const formRef = useRef(null);
  const templateNameRef = useRef(null);
  useEffect(() => {
    showUpload && templateNameRef.current.focus();
  }, [showUpload]);

  useEffect(() => {
    const disable = !templateName.trim().length;
    if (disable && !templateFileDisabled) setTemplateFileDisabled(disable);
    else if (templateFileDisabled) setTemplateFileDisabled(disable);
  }, [templateName]);

  return (
    <Modal show onHide={closeModal} backdrop="static" fullscreen="lg-down" size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{t("NewPersonnelStatement", "Uusi henkilöstökertomus")}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Accordion alwaysOpen defaultActiveKey={["0", "1"]}>
          <Accordion.Item eventKey="0">
            <Accordion.Header>HR henkilöstökertomus malli</Accordion.Header>
            <Accordion.Body>
              <Row>
                <Form.Group as={Col} controlId="templateName" sm="5">
                  <Form.Label>Valitse henkilöstökertomusmalli</Form.Label>
                  <Form.Select {...register("templateId")}>{templateOptions}</Form.Select>
                </Form.Group>

                {location.hostname !== hrEsalary && (
                  <Col className="mt-4">
                    <Button style={{ width: '200px' }}onClick={() => setShowUpload(!showUpload)} size="sm">
                      Lataa uusi malli
                    </Button>
                  </Col>
                )}

                {showUpload && (
                  <Row>
                    <Col>
                      <Form.Group controlId="templateName">
                        <Form.Label>Henkilöstökertomusmallin nimi</Form.Label>
                        <Form.Control
                          ref={templateNameRef}
                          value={templateName}
                          onChange={(e) => setTemplateName(e.target.value)}
                        />
                      </Form.Group>
                      <Form.Group controlId="templateFile">
                        <Form.Label>Mallitiedosto (XLSX)</Form.Label>
                        <Form.Control
                          name="file"
                          type="file"
                          onChange={uploadTemplate}
                          disabled={templateFileDisabled}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                )}
              </Row>
            </Accordion.Body>
          </Accordion.Item>

          <Accordion.Item eventKey="1">
            <Accordion.Header>Raportointijakso</Accordion.Header>
            <Accordion.Body>
              <Form
                ref={formRef}
                onSubmit={handleSubmit(async (data) => {
                  if (data.name && data.start && data.end) {
                    data.templateId = Number(data.templateId);
                    data.start = data.start.slice(0, -3); // Send dates as YYYY-MM
                    data.end = data.end.slice(0, -3);
                    localStorage.setItem(storageKey, JSON.stringify(data));
                    data.language = data.language.toUpperCase();
                    setGeneratingReport(true);
                    try {
                      await statementReportPost(data); // returns created report id
                      closeModal();
                      await fetchData();
                    } catch ({ response }) {
                      response.data.localizedMessages
                        ? dispatch(
                            buildAlertWithLocalizedMessages(
                              "ERROR",
                              "Alert",
                              "Huomio",
                              response.data.localizedMessages,
                            ),
                          )
                        : dispatch(buildAlert("ERROR", response.data.error, response.data.message));
                    }
                    setGeneratingReport(false);
                  }
                })}
              >
                <Row>
                  <Form.Group as={Col} controlId="name">
                    <Form.Label>Henkilöstökertomuksen nimi</Form.Label>
                    <Form.Control {...register("name")} />
                  </Form.Group>

                  <Form.Group as={Col} controlId="language">
                    <Form.Label>Tulostuskieli</Form.Label>
                    <Form.Select {...register("language")}>{localized.languageOptions}</Form.Select>
                  </Form.Group>
                </Row>

                <Row>
                  <Form.Group as={Col} className="mt-4" controlId="start">
                    <DefaultDatePicker
                      id={`${prefix}-start`}
                      name="startDate"
                      label={t("Starting", "Alkaa")}
                      control={control}
                    />
                  </Form.Group>

                  <Form.Group as={Col} className="mt-4" controlId="end">
                    <DefaultDatePicker
                      id={`${prefix}-end`}
                      name="endDate"
                      label={t("Ending", "Päättyy")}
                      control={control}
                    />
                  </Form.Group>
                </Row>

                <Row className="mt-3">
                  <Form.Group as={Col} controlId="prompt">
                    <Form.Label>Kuvaile henkilöstökertomuksen tyyli ja sisältö</Form.Label>
                    <Form.Control
                      style={{ background: "#FFFAEC" }}
                      rows={5}
                      as="textarea"
                      {...register("prompt")}
                      defaultValue={defaultPrompt}
                      placeholder={defaultPrompt}
                    />
                  </Form.Group>
                </Row>
              </Form>
            </Accordion.Body>
          </Accordion.Item>

          <Accordion.Item eventKey="2">
            <Accordion.Header>Henkilöstön rakenne</Accordion.Header>
            <Accordion.Body>
              <PersonnelStructure />
            </Accordion.Body>
          </Accordion.Item>

          <Accordion.Item eventKey="3">
            <Accordion.Header>Henkilöstön osaaminen</Accordion.Header>
            <Accordion.Body>(Tulossa)</Accordion.Body>
          </Accordion.Item>

          <Accordion.Item eventKey="4">
            <Accordion.Header>Henkilöstön poissaolot ja lomat</Accordion.Header>
            <Accordion.Body>(Tulossa)</Accordion.Body>
          </Accordion.Item>

          <Accordion.Item eventKey="5">
            <Accordion.Header>Henkilötuloslaskelma</Accordion.Header>
            <Accordion.Body>(Tulossa)</Accordion.Body>
          </Accordion.Item>

          {/*
        <Accordion.Item eventKey="6">
          <Accordion.Header>Henkilöstötase</Accordion.Header>
          <Accordion.Body>(Tulossa)</Accordion.Body>
        </Accordion.Item>
*/}
        </Accordion>
      </Modal.Body>

      <Modal.Footer style={{ justifyContent: "space-between" }}>
        <Button variant="cancel" onClick={closeModal}>
          {t("Cancel", "Peruuta")}
        </Button>
        <Button disabled={generatingReport} variant="primary" onClick={() => formRef.current.requestSubmit()}>
          {generatingReport && <LoadingButton />} &nbsp;
          {t("Generate", "Luo")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
